import { cur } from '@/lib/currency';
import { useEffect, useState, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, DollarSign, TrendingUp, CheckCircle, Receipt, Clock, Download, Globe, Scale } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ExportButtons } from '@/components/ExportButtons';
import api from '@/lib/api';
import { toast } from 'sonner';
import { NewBadge } from '@/components/NewBadge';
import { useBranchStore } from '@/stores';

import { PlatformOverview } from './platforms/PlatformOverview';
import { BranchSummaryTab } from './platforms/BranchSummaryTab';
import { PlatformsList } from './platforms/PlatformsList';
import { PaymentsTable } from './platforms/PaymentsTable';
import { ReconciliationTab } from './platforms/ReconciliationTab';
import { PlatformFormDialog } from './platforms/PlatformFormDialog';
import { RecordPaymentDialog } from './platforms/RecordPaymentDialog';
import { localToday, toLocalYmd } from '@/lib/dateRange';

export default function PlatformsPage() {
  const { branches, fetchBranches } = useBranchStore();
  const [platforms, setPlatforms] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ platforms: [], totals: {} });
  const [branchSummary, setBranchSummary] = useState([]);
  const [trend, setTrend] = useState({ series: [], platforms: [] });
  const [loading, setLoading] = useState(true);
  // iter960 — universal report filters (branch + date range)
  const [branchId, setBranchId] = useState('all');
  const [startDate, setStartDate] = useState(() => toLocalYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [endDate, setEndDate] = useState(localToday());
  const [showPlatformForm, setShowPlatformForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [calculatedPayment, setCalculatedPayment] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const [platformForm, setPlatformForm] = useState({
    name: '', name_ar: '', commission_rate: '', payment_terms: 'weekly',
    contact_email: '', contact_phone: '', notes: '', bank_accounts: []
  });

  const [paymentForm, setPaymentForm] = useState({
    platform_id: '', payment_date: localToday(),
    period_start: '', period_end: '', total_sales: '', commission_paid: '',
    amount_received: '', payment_method: 'bank_transfer', reference_number: '', notes: '',
    received_branch_id: ''
  });

  useEffect(() => { fetchData(); fetchBranches(); }, [fetchBranches]);
  // iter960 — auto-refetch when the branch changes (dates apply via button)
  const _firstBranch = useRef(true);
  useEffect(() => {
    if (_firstBranch.current) { _firstBranch.current = false; return; }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const fetchData = async () => {
    try {
      const sp = new URLSearchParams();
      if (startDate) sp.set('start_date', startDate);
      if (endDate) sp.set('end_date', endDate);
      if (branchId && branchId !== 'all') sp.set('branch_id', branchId);
      const qs = sp.toString() ? `?${sp.toString()}` : '';
      const [platformsRes, paymentsRes, summaryRes, branchRes] = await Promise.all([
        api.get('/platforms'),
        api.get('/platform-payments'),
        api.get(`/platforms/summary${qs}`),
        api.get(`/platforms/branch-summary${qs}`).catch(() => ({ data: [] }))
      ]);
      setPlatforms(platformsRes.data);
      setPayments(paymentsRes.data);
      setSummary(summaryRes.data);
      setBranchSummary(branchRes.data || []);
      api.get(`/platforms/trend${qs}`).then(r => setTrend(r.data || { series: [], platforms: [] })).catch(() => {});
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateExpected = async () => {
    if (!paymentForm.platform_id || !paymentForm.period_start || !paymentForm.period_end) {
      toast.error('Please select platform and date range first'); return;
    }
    setCalculating(true);
    try {
      const res = await api.get(`/platform-payments/calculate?platform_id=${paymentForm.platform_id}&period_start=${paymentForm.period_start}&period_end=${paymentForm.period_end}`);
      const calc = res.data;
      setCalculatedPayment(calc);
      setPaymentForm(prev => ({ ...prev, total_sales: calc.total_sales.toString(), commission_paid: calc.calculated_commission.toString(), amount_received: calc.expected_amount.toString() }));
      toast.success(`Found ${calc.sales_count} sales totaling ${cur()} ${calc.total_sales.toLocaleString()}`);
    } catch { toast.error('Failed to calculate'); }
    finally { setCalculating(false); }
  };

  const recalcFromTotalSales = (totalStr) => {
    const total = parseFloat(totalStr) || 0;
    const platform = platforms.find(p => p.id === paymentForm.platform_id);
    const rate = parseFloat(platform?.commission_rate || 0);
    const commission = Math.round(total * (rate / 100) * 100) / 100;
    const received = Math.round((total - commission) * 100) / 100;
    setPaymentForm(prev => ({ ...prev, total_sales: totalStr, commission_paid: commission > 0 ? commission.toString() : '', amount_received: received > 0 ? received.toString() : '' }));
  };

  const seedDefaults = async () => {
    try { const res = await api.post('/platforms/seed-defaults'); toast.success(res.data.message); fetchData(); }
    catch { toast.error('Failed to seed platforms'); }
  };

  const handlePlatformSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPlatform) { await api.put(`/platforms/${editingPlatform.id}`, platformForm); toast.success('Platform updated'); }
      else { await api.post('/platforms', platformForm); toast.success('Platform added'); }
      setShowPlatformForm(false); setEditingPlatform(null); resetPlatformForm(); fetchData();
    } catch { toast.error('Failed to save platform'); }
  };

  const editPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      platform_id: payment.platform_id,
      payment_date: payment.payment_date?.split('T')[0] || '',
      period_start: payment.period_start?.split('T')[0] || '',
      period_end: payment.period_end?.split('T')[0] || '',
      total_sales: payment.total_sales?.toString() || '',
      commission_paid: payment.commission_paid?.toString() || '',
      amount_received: payment.amount_received?.toString() || '',
      payment_method: payment.payment_method || 'bank_transfer',
      reference_number: payment.reference_number || '',
      notes: payment.notes || '',
    });
    setShowPaymentForm(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentForm.platform_id) { toast.error('Please select a platform'); return; }
    if (!paymentForm.amount_received || parseFloat(paymentForm.amount_received) <= 0) { toast.error('Amount received must be greater than 0'); return; }
    try {
      const payload = { ...paymentForm, total_sales: parseFloat(paymentForm.total_sales) || 0, commission_paid: parseFloat(paymentForm.commission_paid) || 0, amount_received: parseFloat(paymentForm.amount_received) || 0 };
      if (editingPayment) {
        await api.put(`/platform-payments/${editingPayment.id}`, payload);
        toast.success('Payment updated');
      } else {
        try {
          await api.post('/platform-payments', payload);
        } catch (err) {
          if (err.response?.status !== 409 || !String(err.response?.data?.detail || '').includes('additional')) throw err;
          if (!window.confirm(`${err.response.data.detail}.\n\nRecord it as an additional payment?`)) return;
          await api.post('/platform-payments', { ...payload, confirm_additional: true });
        }
        toast.success('Payment recorded successfully');
      }
      setShowPaymentForm(false); setEditingPayment(null); resetPaymentForm(); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save payment'); }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment? Linked sales will be un-settled and become pending again.')) return;
    try { await api.delete(`/platform-payments/${paymentId}`); toast.success('Payment deleted successfully'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete payment'); }
  };

  const deletePlatform = async (id) => {
    if (!window.confirm('Delete this platform?')) return;
    try { await api.delete(`/platforms/${id}`); toast.success('Platform deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const resetPlatformForm = () => setPlatformForm({ name: '', name_ar: '', commission_rate: '', commission_vat_rate: '', commission_basis: null, effective_from: '', payment_terms: 'weekly', contact_email: '', contact_phone: '', notes: '', bank_accounts: [] });
  const resetPaymentForm = () => setPaymentForm({ platform_id: '', payment_date: localToday(), period_start: '', period_end: '', total_sales: '', commission_paid: '', amount_received: '', payment_method: 'bank_transfer', reference_number: '', notes: '' });

  const editPlatform = (platform) => {
    setEditingPlatform(platform);
    setPlatformForm({ name: platform.name, name_ar: platform.name_ar || '', commission_rate: platform.commission_rate || '', commission_vat_rate: platform.commission_vat_rate ?? '', commission_basis: platform.commission_basis || null, effective_from: '', payment_terms: platform.payment_terms || 'weekly', contact_email: platform.contact_email || '', contact_phone: platform.contact_phone || '', notes: platform.notes || '', bank_accounts: platform.bank_accounts || [] });
    setShowPlatformForm(true);
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  const { totals } = summary;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200/50">
                <Globe size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground flex items-center gap-2" data-testid="platforms-page-title">
                  Online Platforms
                  <NewBadge since="2026-04-22" featureId="platforms-bank-accounts" size="xs" label="Bank Accounts" />
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">Delivery platform integrations</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">Manage delivery platforms: HungerStation, Jahez, ToYou, etc.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {platforms.length === 0 && (
              <Button variant="outline" onClick={seedDefaults} className="rounded-xl"><Download size={14} className="mr-1" />Load Default Platforms</Button>
            )}
            <Button variant="outline" onClick={() => setShowPaymentForm(true)} className="rounded-xl" data-testid="record-payment-btn"><DollarSign size={14} className="mr-1" />Record Payment</Button>
            <Button onClick={() => { resetPlatformForm(); setEditingPlatform(null); setShowPlatformForm(true); }} className="rounded-xl bg-purple-600 hover:bg-purple-700" data-testid="add-platform-btn"><Plus size={14} className="mr-1" />Add Platform</Button>
          </div>
        </div>

        {/* iter960 — Universal report filters + scoped export */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); }}>
              <SelectTrigger className="w-44 h-9 text-xs" data-testid="platforms-branch-filter"><SelectValue placeholder="All Branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-36 h-9" data-testid="platforms-start-date" />
            <span className="text-muted-foreground text-xs">to</span>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-36 h-9" data-testid="platforms-end-date" />
            <Button size="sm" onClick={fetchData} className="h-9 rounded-xl" data-testid="platforms-apply-btn">Apply</Button>
            <div className="ml-auto">
              <ExportButtons branchIds={branchId && branchId !== 'all' ? [branchId] : []}
                dataType="platforms"
                label="Online Platforms"
                startDate={startDate}
                endDate={endDate}
                filters={{ branch_id: branchId === 'all' ? null : branchId }}
                testid="platforms-export"
              />
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><TrendingUp size={16} className="text-purple-600" /><span className="text-sm text-muted-foreground">Total Sales</span></div>
              <div className="text-2xl font-bold text-purple-700">{cur()} {(totals.total_sales || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-success/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><CheckCircle size={16} className="text-success" /><span className="text-sm text-muted-foreground">Received</span></div>
              <div className="text-2xl font-bold text-success">{cur()} {(totals.total_received || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-warning/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><Receipt size={16} className="text-warning" /><span className="text-sm text-muted-foreground">Commission</span></div>
              <div className="text-2xl font-bold text-warning">{cur()} {(totals.total_commission || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-error/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><Clock size={16} className="text-error" /><span className="text-sm text-muted-foreground">Pending</span></div>
              <div className="text-2xl font-bold text-error">{cur()} {(totals.total_pending || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="rounded-xl">
            <TabsTrigger value="overview" className="rounded-lg">Overview</TabsTrigger>
            <TabsTrigger value="branches" className="rounded-lg">By Branch</TabsTrigger>
            <TabsTrigger value="platforms" className="rounded-lg">Platforms</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-lg">Payments</TabsTrigger>
            <TabsTrigger value="reconciliation" className="rounded-lg" data-testid="reconciliation-tab-trigger">
              <Scale size={14} className="mr-1" />Reconciliation
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <PlatformOverview summary={summary} seedDefaults={seedDefaults} trend={trend} />
          </TabsContent>
          <TabsContent value="branches" className="space-y-4 mt-4">
            <BranchSummaryTab branchSummary={branchSummary} />
          </TabsContent>
          <TabsContent value="platforms" className="space-y-4 mt-4">
            <PlatformsList platforms={platforms} onEdit={editPlatform} onDelete={deletePlatform} />
          </TabsContent>
          <TabsContent value="payments" className="mt-4">
            <PaymentsTable payments={payments} onDelete={deletePayment} onEdit={editPayment} />
          </TabsContent>
          <TabsContent value="reconciliation" className="mt-4">
            <ReconciliationTab branchId={branchId} startDate={startDate} endDate={endDate} />
          </TabsContent>
        </Tabs>

        <PlatformFormDialog open={showPlatformForm} onOpenChange={setShowPlatformForm} editingPlatform={editingPlatform} platformForm={platformForm} setPlatformForm={setPlatformForm} onSubmit={handlePlatformSubmit} />
        <RecordPaymentDialog open={showPaymentForm} onOpenChange={(o) => { setShowPaymentForm(o); if (!o) setEditingPayment(null); }} platforms={platforms} branches={branches} paymentForm={paymentForm} setPaymentForm={setPaymentForm} calculatedPayment={calculatedPayment} setCalculatedPayment={setCalculatedPayment} calculating={calculating} onCalculate={calculateExpected} onRecalcFromTotalSales={recalcFromTotalSales} onSubmit={handlePaymentSubmit} isEditing={!!editingPayment} />
      </div>
    </DashboardLayout>
  );
}
